import { REACTOR_STORM_RULES } from "../config/gameplay";
import type { ReactorStormView } from "../game/events/reactor-storm";

const stormColors = {
  cyan: "#35f0ff",
  pink: "#ff3ba7",
  lime: "#70ff8a",
  gold: "#ffd34a"
} as const;

export function drawReactorStorm(context: CanvasRenderingContext2D, storm: ReactorStormView): void {
  if (!storm.active) {
    return;
  }

  drawStormField(context);
  for (const board of storm.boards) {
    const color = stormColors[board.color];
    context.save();
    context.translate(board.position.x, board.position.y);
    context.rotate(board.rotationRadians);
    context.fillStyle = "rgba(13, 24, 31, 0.94)";
    context.strokeStyle = colorWithAlpha(color, 0.86);
    context.lineWidth = 3;
    context.shadowBlur = 20;
    context.shadowColor = colorWithAlpha(color, 0.46);
    roundedRect(context, -REACTOR_STORM_RULES.boardLength / 2, -REACTOR_STORM_RULES.boardThickness / 2, REACTOR_STORM_RULES.boardLength, REACTOR_STORM_RULES.boardThickness, 6);
    context.fill();
    context.stroke();
    context.restore();
  }

  for (const core of storm.cores) {
    const color = stormColors[core.color];
    context.save();
    context.fillStyle = core.isOvercharged ? "#ffd34a" : color;
    context.shadowBlur = core.isOvercharged ? 24 : 14;
    context.shadowColor = core.isOvercharged ? "rgba(255, 211, 74, 0.68)" : colorWithAlpha(color, 0.5);
    context.beginPath();
    context.arc(core.position.x, core.position.y, core.radius * 1.35, 0, Math.PI * 2);
    context.fill();
    if (core.stormMultiplier > 1) {
      context.strokeStyle = "rgba(233, 251, 255, 0.78)";
      context.lineWidth = 2;
      context.stroke();
    }
    context.restore();
  }
}

function drawStormField(context: CanvasRenderingContext2D): void {
  const gradient = context.createRadialGradient(0, 0, 80, 0, 0, 585);
  gradient.addColorStop(0, "rgba(139, 92, 255, 0.16)");
  gradient.addColorStop(0.54, "rgba(255, 59, 167, 0.1)");
  gradient.addColorStop(1, "rgba(5, 8, 11, 0)");
  context.save();
  context.fillStyle = gradient;
  context.beginPath();
  context.arc(0, 0, 585, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
}

function colorWithAlpha(hexColor: string, alpha: number): string {
  const red = Number.parseInt(hexColor.slice(1, 3), 16);
  const green = Number.parseInt(hexColor.slice(3, 5), 16);
  const blue = Number.parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
